import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type Hls } from '@/scripts/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { percentageSeen } from '@/scripts/theme/animations'
import { type ArtDirection } from '@/scripts/core/art-direction'
import { isSafari } from '../utils/helpers'

function isVideoPlaying(video: HTMLVideoElement): boolean {
	if( !video ) return true;
	return !video.paused && !video.ended && video.currentTime > 0
}

export class UcoastVideo extends UcoastEl {
	static htmlSelector = 'ucoast-video'
	videoEl: HTMLVideoElement
	hlsReady: boolean
	hlsSource?: string
	mp4Source?: string
	initialized = false
	usingMp4Source: boolean
	hlsInstance: Hls
	eventDriven:boolean
	eventEnabled:boolean
	eagerLoad:boolean
	pageLoadRequired: boolean
	pageLoaded: boolean
	private activationObserver: IntersectionObserver | null = null
	private lcpObserver: PerformanceObserver | null = null
	private initialPaintDone = false
	private activationHandled = false
	wrapper?: ArtDirection
	onPlay?: (video: UcoastVideo) => void
	onPause?: (video: UcoastVideo) => void

	constructor() {
		super()
		this.videoEl = q.rs<HTMLVideoElement>('video', this)
		this.hlsSource = this.videoEl.getAttribute('data-hls-src') ?? undefined
		this.hlsReady = false
		this.mp4Source = this.videoEl.getAttribute('data-mp4-src') ?? undefined
		this.usingMp4Source = false
		this.initialized = true
		this.eventDriven = this.hasAttribute('data-event-driven')
		this.eventEnabled = false
		this.eagerLoad = this.hasAttribute('data-eager-load')
		this.pageLoadRequired = this.hasAttribute('data-load-after-page')
		this.pageLoaded = !this.pageLoadRequired || document.readyState === 'complete'
		this.wrapper = q.oc(this, 'art-direction');

		this.handleCustomControls();
		this.setupEvents();
		
		// For eager load videos, set the source immediately
		if (this.eagerLoad && this.pageLoaded) {
			this.loadSourceImmediately()
		}

		if (this.pageLoadRequired && !this.pageLoaded) {
			this.watchForActivation()
		}
	}

	private onPageLoad = () => {
		this.pageLoaded = true
		this.onConnectedCallback()
		void this.play()
	}

	/**
	 * Poster-first activation.
	 *
	 * The hero poster (LCP) is rendered eagerly by the template, so it paints
	 * before any video data is requested. We attach the video source and play
	 * once BOTH conditions are met:
	 *   1. the element is in the viewport (IntersectionObserver), and
	 *   2. the initial paint is done — signaled by the LCP PerformanceObserver,
	 *      with the `load` event kept as a fallback for browsers/synthetic tests
	 *      that never report LCP.
	 *
	 * This starts the video right after LCP instead of waiting for the full
	 * `window.load` event, which is delayed by third-party tracking scripts.
	 */
	private watchForActivation() {
		if (typeof IntersectionObserver !== 'undefined') {
			this.activationObserver = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						if (entry.isIntersecting) {
							this.maybeActivate()
							return
						}
					}
				},
				{ threshold: 0 },
			)
			this.activationObserver.observe(this.videoEl)
		}

		window.addEventListener('load', this.onActivationReady, { once: true })

		try {
			this.lcpObserver = new PerformanceObserver((list) => {
				for (const entry of list.getEntries()) {
					if (entry.entryType === 'largest-contentful-paint') {
						this.onActivationReady()
						return
					}
				}
			})
			this.lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true })
		} catch {
			// LCP unsupported here — the load listener covers activation.
		}
	}

	private onActivationReady = () => {
		if (this.activationHandled) return
		this.initialPaintDone = true
		this.lcpObserver?.disconnect()
		this.lcpObserver = null
		this.maybeActivate()
	}

	private maybeActivate = () => {
		if (this.activationHandled) return
		if (!this.initialPaintDone || !this.isTouchingViewport()) return
		this.activationHandled = true
		this.teardownActivationWatchers()
		this.onPageLoad()
	}

	private teardownActivationWatchers() {
		this.activationObserver?.disconnect()
		this.activationObserver = null
		this.lcpObserver?.disconnect()
		this.lcpObserver = null
		window.removeEventListener('load', this.onActivationReady)
	}

	private setupEvents() {
		this.videoEl.addEventListener('play', () => {
			this.dispatchEvent(new CustomEvent('play', { bubbles: true, detail: { video: this } }))
			if (this.onPlay) this.onPlay(this)
		})

		this.videoEl.addEventListener('pause', () => {
			this.dispatchEvent(new CustomEvent('pause', { bubbles: true, detail: { video: this } }))
			if (this.onPause) this.onPause(this)
		})
	}
	
	loadSourceImmediately() {
		if (!this.hlsSource && !this.usingMp4Source && this.mp4Source) {
			this.videoEl.src = this.mp4Source
			this.usingMp4Source = true
		}
	}

	override async connectedCallback() {
		super.connectedCallback()
		this.onConnectedCallback()
	}

	onConnectedCallback() {
		if (!window?.Ucoast?.mediaManager) {
			this.markForRetry()
		}
		if (this.pageLoadRequired && !this.pageLoaded) {
			window.Ucoast?.mediaManager?.addVideo(this)
			return
		}
		if (window?.Ucoast?.mediaManager?.hlsLibraryLoaded) {
			void this.onLibraryLoad()
		} else if (!this.hlsSource && this.mp4Source) {
			window.Ucoast?.mediaManager?.addVideo(this)
		} else if (
			this.hlsSource &&
			window.Ucoast?.mediaManager?.hlsRequired !== undefined &&
			window.Ucoast.mediaManager.hlsRequired === false
		) {
			window?.Ucoast?.mediaManager?.addVideo(this)
		}
		
		// For eager load videos with HLS, add to manager immediately so source can be loaded
		if (this.eagerLoad && this.hlsSource && !window.Ucoast?.mediaManager?.hlsLibraryLoaded) {
			window.Ucoast?.mediaManager?.addVideo(this)
		}
	}

	markForRetry() {
		this.setAttribute('data-retry', 'true')
	}

	// used by mediaManager to retry initialization
	unMarkForRetry() {
		this.removeAttribute('data-retry')
	}

	override async disconnectedCallback() {
		super.disconnectedCallback()
		this.teardownActivationWatchers()
		if (this.hlsInstance) {
			this.hlsInstance.destroy()
			window.Ucoast.mediaManager.removeVideo(this)
		}
		// Cleanup modal if it was moved to body
		const video = this.wrapper?.closest('.video--wrapper') as HTMLElement | null
		if (video && (video as any).__videoModalCleanup) {
			;(video as any).__videoModalCleanup()
			delete (video as any).__videoModalCleanup
		}
	}

	// both playEvent methods are used by remote components
	async playEventOn() {
		if (!this.eventDriven) return
		this.eventEnabled = true
		await this.play()
	}

	playEventOff() {
		this.eventDriven = true // sometimes we need to convert auto-play to event-driven
		this.eventEnabled = false
		this.pause()
	}

	isTouchingViewport() {
		const rect = this.getBoundingClientRect()

		const isVisible =
			rect.top < window.innerHeight &&
			rect.bottom > 0 &&
			rect.left < window.innerWidth &&
			rect.right > 0

		console.log('checked isTouchingViewport', isVisible)

		return isVisible
	}

	async play() {
		// all conditions that should prevent the video from playing must go here
		if (this.pageLoadRequired && !this.pageLoaded) return
		if (!this.isTouchingViewport()) return
		if (this.eventDriven && !this.eventEnabled) return
		if (this.isHiddenForViewport()) return
		if (isVideoPlaying(this.videoEl)) return
		if (this.isWaitingForHlsLibrary()) return
		if (percentageSeen(this.videoEl) <= 0) return

		// check for updates that must happen before playing that can be done synchronously
		if (!this.hlsSource && !this.usingMp4Source && this.mp4Source) {
			this.videoEl.src = this.mp4Source
			this.usingMp4Source = true
		}
		if (
			this.hlsSource &&
			!window.Ucoast.mediaManager.hlsRequired &&
			!this.videoEl.currentSrc
		) {
			this.videoEl.src = this.hlsSource
		}
		// play video w/native html5 video element
		console.log('playing')
		await this.videoEl.play()
		this.revealVideoWhenReady()
	}

	private revealVideoWhenReady() {
		const reveal = () => {
			this.setAttribute('data-uc-has-played', 'true')
			this.wrapper?.onMediaLoad()
		}
		const videoWithFrameCallback = this.videoEl as HTMLVideoElement & {
			requestVideoFrameCallback?: (callback: () => void) => number
		}

		if (videoWithFrameCallback.requestVideoFrameCallback) {
			videoWithFrameCallback.requestVideoFrameCallback(reveal)
			return
		}

		if (this.videoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
			requestAnimationFrame(reveal)
			return
		}

		this.videoEl.addEventListener('loadeddata', () => requestAnimationFrame(reveal), {
			once: true,
		})
	}
	pause() {
		if (isVideoPlaying(this.videoEl)) {
			console.log('pausin')
			this.videoEl.pause()
		}
	}

	isWaitingForHlsLibrary() {
		if (!this.hlsSource) return false
		if (!window.Ucoast.mediaManager.hlsRequired) return false
		if (!this.hlsReady) return true
		return !window.Ucoast.mediaManager.hlsLibraryLoaded
	}

	async onLibraryLoad() {
		// don't run if this isn't an hls video or if hls is already initalized for this element
		if (!this.hlsSource) return
		if (this.hlsInstance) return
		if (this.pageLoadRequired && !this.pageLoaded) return
		// initialize hls - this is a 3ms process that doesn't load any video data until a video is played
		this.hlsInstance = new window.Ucoast.mediaManager.Hls.default()
		this.hlsInstance.on('hlsManifestParsed', () => {
			this.hlsReady = true
			// For eager load videos, don't auto-play on manifest parsed - let IntersectionObserver handle it
			if (!this.eagerLoad) {
				void this.play()
			}
		})
		this.videoEl.src = this.hlsSource
		await this.hlsInstance.loadSource(this.hlsSource)
		await this.hlsInstance.attachMedia(this.videoEl)
		// all videos present in DOM at page load are added to the mediaManager.videos array, but if this video was added to the DOM later, it needs to be added to the array

		window.Ucoast.mediaManager.addVideo(this)
	}

	isHiddenForViewport() {
		return (
			this.videoEl.getBoundingClientRect().height < 1 ||
			this.videoEl.getBoundingClientRect().width < 1
		)
	}

	handleCustomControls() {
		const video = this.wrapper?.closest('.video--wrapper') as HTMLElement | null;

		if (!video) return

		if (video.hasAttribute('data-controls-initialized')) return
		video.setAttribute('data-controls-initialized', 'true')

		const isModal = video.classList.contains('video--popup')
		let originalParent: HTMLElement | null = null
		let originalNextSibling: Node | null = null
		let isMovedToBody = false

		const getPlayingVideo = (): UcoastVideo | null => {
			const videos = q.ol<UcoastVideo>('ucoast-video', video)
			if (!videos || videos.length === 0) return null
			
			for (let i = 0; i < videos.length; i++) {
				const ucoastVideo = videos[i]
				if (isVideoPlaying(ucoastVideo.videoEl)) {
					return ucoastVideo
				}
			}
			return null
		}
		const getActiveVideo = (): UcoastVideo | null => {
			const videos = q.ol<UcoastVideo>('ucoast-video', video)
			if (!videos || videos.length === 0) return null
			const playingVideo = getPlayingVideo()
			if (playingVideo) return playingVideo

			for (let i = 0; i < videos.length; i++) {
				const ucoastVideo = videos[i]
				if (!ucoastVideo.isHiddenForViewport()) {
					return ucoastVideo
				}
			}

			return videos[0]
		}

		const moveToBody = () => {
			if (!isModal || isMovedToBody) return
			
			originalParent = video.parentElement
			originalNextSibling = video.nextSibling
			document.body.appendChild(video)
			isMovedToBody = true
			document.body.classList.add('overflow-hidden')
		}

		const moveBackToOriginal = () => {
			if (!isModal || !isMovedToBody || !originalParent) return
			
			if (originalNextSibling) {
				originalParent.insertBefore(video, originalNextSibling)
			} else {
				originalParent.appendChild(video)
			}
			
			isMovedToBody = false
			originalParent = null
			originalNextSibling = null
			document.body.classList.remove('overflow-hidden')
		}

		const trigger = video.querySelector('.play--pause-toggle') as HTMLButtonElement
		const containerTrigger = video.querySelector('.video--container') as HTMLElement
		const audioToggle = video.querySelector('.js-toggle-audio') as HTMLButtonElement | null
		const setAudioToggleFromActiveVideo = (targetVideo?: UcoastVideo | null) => {
			if (!audioToggle) return
			const activeVideo = targetVideo ?? getActiveVideo()
			if (!activeVideo) {
				audioToggle.classList.remove('audio-toggled')
				return
			}
			audioToggle.classList.toggle('audio-toggled', !activeVideo?.videoEl?.muted)
		}

		[trigger, containerTrigger].forEach((el) => {
				if (!el) return

				el.addEventListener('click', async () => {
					const activeVideo = getActiveVideo()
					if (!activeVideo) return
					const activeVideoEl = activeVideo.videoEl
					activeVideo.eventEnabled = true

					if (isVideoPlaying(activeVideoEl)) {
						activeVideo.pause()
						setAudioToggleFromActiveVideo(activeVideo)
						video.classList.remove('is-playing')
						// Keep modal open when pausing - only close on close button
					} else {
						if (isModal) {
							moveToBody()
							// Wait for DOM to update and ensure video is ready
							await new Promise(resolve => requestAnimationFrame(resolve))
							await new Promise(resolve => requestAnimationFrame(resolve))
						}
						
						// For modal videos, bypass restrictive play checks and play directly
						if (isModal) {
							// Set up video source if needed
							if (
								!activeVideo.hlsSource &&
								!activeVideo.usingMp4Source &&
								activeVideo.mp4Source
							) {
								activeVideo.videoEl.src = activeVideo.mp4Source
								activeVideo.usingMp4Source = true
							}
							
							// For HLS videos, initialize if needed
							if (activeVideo.hlsSource && !activeVideo.hlsInstance) {
								if (window.Ucoast?.mediaManager?.hlsLibraryLoaded) {
									// Initialize HLS
									activeVideo.hlsInstance = new window.Ucoast.mediaManager.Hls.default()
									activeVideo.hlsInstance.on('hlsManifestParsed', () => {
										activeVideo.hlsReady = true
									})
									activeVideo.videoEl.src = activeVideo.hlsSource
									await activeVideo.hlsInstance.loadSource(activeVideo.hlsSource)
									await activeVideo.hlsInstance.attachMedia(activeVideo.videoEl)
									window.Ucoast?.mediaManager?.addVideo(activeVideo)
									// Wait for manifest to be parsed before playing
									if (!activeVideo.hlsReady) {
										await new Promise((resolve) => {
											activeVideo.hlsInstance!.on('hlsManifestParsed', () => {
												activeVideo.hlsReady = true
												resolve(undefined)
											}, { once: true })
										})
									}
								} else if (activeVideo.isWaitingForHlsLibrary()) {
									// HLS library not loaded yet, use regular play method
									await activeVideo.play()
									video.classList.add('modal--player')
									video.classList.add('is-playing')
									return
								}
							}
							
							// Play the video directly (bypassing viewport/visibility checks for modal)
							try {
								if (!isVideoPlaying(activeVideo.videoEl)) {
									await activeVideo.videoEl.play()
									activeVideo.setAttribute('data-uc-has-played', 'true')
									if (activeVideo.wrapper) {
										activeVideo.wrapper.onMediaLoad()
									}
								}
							} catch (error) {
								console.error('Error playing video:', error)
								// Fallback to regular play method if direct play fails
								await activeVideo.play()
							}
						} else {
							await activeVideo.play()
						}
						video.classList.add('modal--player')
						video.classList.add('is-playing')
						setAudioToggleFromActiveVideo(activeVideo)
					}
				})
			});

		const closeVideo = video.querySelector(
			'.js-close-video'
		) as HTMLButtonElement

		const handleVideoClose = () => {
			const videos = q.ol<UcoastVideo>('ucoast-video', video)
			if (videos) {
				for (let i = 0; i < videos.length; i++) {
					const ucoastVideo = videos[i]
					const videoEl = ucoastVideo.videoEl
					if (isVideoPlaying(videoEl)) {
						ucoastVideo.pause()
					}
					videoEl.currentTime = 0
				}
			}
			video.classList.remove('is-playing')
			video.classList.remove('modal--player')
			
			if (isModal) {
				moveBackToOriginal()
			}
		};

		closeVideo?.addEventListener('click', handleVideoClose)

		// Handle ESC key and click outside for modal
		if (isModal) {
			const handleEscape = (e: KeyboardEvent) => {
				if (e.key === 'Escape' && video.classList.contains('modal--player')) {
					handleVideoClose()
				}
			}

			const handleClickOutside = (e: MouseEvent) => {
				if (video.classList.contains('modal--player') && isMovedToBody) {
					const target = e.target as HTMLElement
					// Close if clicking on the video wrapper or container (which has pointer-events: none)
					// but not if clicking on controls
					const clickedControls = target.closest('.video--controls')
					if (!clickedControls && (target === video || target.closest('.video--container'))) {
						handleVideoClose()
					}
				}
			}

			document.addEventListener('keydown', handleEscape)
			video.addEventListener('click', handleClickOutside)

			// Store cleanup function on video element for later removal
			;(video as any).__videoModalCleanup = () => {
				if (isMovedToBody) {
					moveBackToOriginal()
				}
				document.removeEventListener('keydown', handleEscape)
				video.removeEventListener('click', handleClickOutside)
			}
		}

		const progressBar = video.querySelector(
			'#progress-container'
		) as HTMLElement
		const progress = video?.querySelector('#progress') as HTMLElement

		if (progressBar && progress) {
			const getVideoWithDuration = (): UcoastVideo | null => {
				const videos = q.ol<UcoastVideo>('ucoast-video', video)
				if (!videos || videos.length === 0) return null
				
				for (let i = 0; i < videos.length; i++) {
					const ucoastVideo = videos[i]
					if (isVideoPlaying(ucoastVideo.videoEl) && ucoastVideo.videoEl.duration) {
						return ucoastVideo
					}
				}
				
				for (let i = 0; i < videos.length; i++) {
					const ucoastVideo = videos[i]
					if (ucoastVideo.videoEl.duration) {
						return ucoastVideo
					}
				}
				
				return null
			}

			const updateProgress = () => {
				const videoWithDuration = getVideoWithDuration()
				if (videoWithDuration) {
					const videoEl = videoWithDuration.videoEl
					if (videoEl.duration) {
						const percent = (videoEl.currentTime / videoEl.duration) * 100;
						progress.style.width = `${percent}%`;
					}
				}
			}

			const videos = q.ol<UcoastVideo>('ucoast-video', video)
			if (videos) {
				for (let i = 0; i < videos.length; i++) {
					const videoEl = videos[i].videoEl
					videoEl.addEventListener('timeupdate', updateProgress)
					videoEl.addEventListener('seeked', updateProgress)
					videoEl.addEventListener('loadedmetadata', updateProgress)
				}
			}

			progressBar.addEventListener('click', e => {
				const videoWithDuration = getVideoWithDuration()
				if (!videoWithDuration) return

				const videoEl = videoWithDuration.videoEl
				const width = progressBar.clientWidth;
				const clickX = e.offsetX;
				const duration = videoEl.duration;

				if (duration) {
					videoEl.currentTime = (clickX / width) * duration
				}
			})
		}

		audioToggle?.addEventListener('click', () => {
			const activeVideo = getActiveVideo()
			if (activeVideo) {
				const activeVideoEl = activeVideo.videoEl
				activeVideoEl.muted = !activeVideoEl.muted
				setAudioToggleFromActiveVideo(activeVideo)
			}
		})

		setAudioToggleFromActiveVideo()
	}
}


document.addEventListener('click', (e) => {
	const target = e.target as HTMLElement
	const isAudioToggler = target.closest('.js-toggle-audio');
	const videoContainer = target.closest('.video--popup') as HTMLDivElement
	if( !videoContainer || !isAudioToggler ) return
	const videoEl = videoContainer.querySelector('video') as HTMLVideoElement
	if( !videoEl ) return
	videoEl.muted = !videoEl.muted
	if (isSafari()) {
		videoEl.defaultMuted = false
		videoEl.play()
	}
	isAudioToggler.classList.toggle('audio-toggled', !videoEl.muted)
})